import { useState } from 'react'
import { AppError } from '@shared/api/error'
import { useFeatureFlag } from '@shared/feature-flags/useFeatureFlag'
import { getUserFacingError } from '@shared/lib/errors/user-facing-error'
import { useQueryClient } from '@tanstack/react-query'
import {
  createKycOnboardingSession,
  fetchKycVerification,
  syncKycVerification,
  verificationKeys,
} from '../api/verification'
import type { KycOnboarding, KycOnboardingSession } from '../api/verification'

export type EmbeddedFeedback = 'in_review' | 'needs_action' | 'done' | 'error' | null

const POST_EXIT_POLL_INTERVAL_MS = 5_000
const POST_EXIT_POLL_LIMIT_MS = 60_000

export function useEmbeddedOnboarding() {
  const registrationFeedbackEnabled = useFeatureFlag('connectRegistrationFeedback')
  const queryClient = useQueryClient()
  const [isOpen, setIsOpen] = useState(false)
  const [session, setSession] = useState<KycOnboardingSession | null>(null)
  const [sessionKey, setSessionKey] = useState<string | undefined>()
  const [isLoading, setIsLoading] = useState(false)
  const [isSyncing, setIsSyncing] = useState(false)
  const [error, setError] = useState<string | null>(null)
  const [feedback, setFeedback] = useState<EmbeddedFeedback>(null)
  const [feedbackUpdatedAt, setFeedbackUpdatedAt] = useState<number | null>(null)

  async function refreshVerification() {
    await queryClient.fetchQuery({
      queryKey: verificationKeys.view,
      queryFn: fetchKycVerification,
    })
  }

  async function open() {
    setIsLoading(true)
    setError(null)
    setFeedback(null)
    setFeedbackUpdatedAt(null)
    try {
      const nextSession = await createKycOnboardingSession()
      setSession(nextSession)
      if (registrationFeedbackEnabled) setSessionKey(nextSession.clientSecret)
      setIsOpen(true)
    } catch (requestError) {
      try {
        if (
          requestError instanceof AppError &&
          requestError.status === 409 &&
          requestError.code === 'nothing_to_collect'
        ) {
          await refreshVerification()
        } else if (
          requestError instanceof AppError &&
          requestError.status === 409 &&
          requestError.code === 'not_linked'
        ) {
          await Promise.all([
            refreshVerification(),
            queryClient.invalidateQueries({ queryKey: ['kyc', 'enrollment'] }),
          ])
        } else {
          setError(
            registrationFeedbackEnabled
              ? describeOpeningError(requestError)
              : 'Não foi possível abrir esta etapa. Tente novamente em alguns instantes.',
          )
        }
      } catch (refreshError) {
        if (!registrationFeedbackEnabled) throw refreshError
        setError('Não foi possível atualizar esta etapa. Tente novamente em alguns instantes.')
      }
    } finally {
      setIsLoading(false)
    }
  }

  async function retry() {
    await open()
  }

  async function close() {
    setIsOpen(false)
    setSession(null)
    setSessionKey(undefined)
    setError(null)
    setFeedback(null)
    setFeedbackUpdatedAt(null)
    setIsSyncing(true)
    try {
      const view = await syncKycVerification()
      queryClient.setQueryData(verificationKeys.view, view)
      const nextFeedback = feedbackFromView(
        view.onboarding,
        Boolean(view.verification?.syncFailure),
        view.verification?.requirements.embedded.length ?? 0,
      )
      setFeedback(nextFeedback)
      setFeedbackUpdatedAt(queryClient.getQueryState(verificationKeys.view)?.dataUpdatedAt ?? null)
      if (nextFeedback !== 'error' && nextFeedback !== 'done') {
        void pollAfterExit(view.onboarding.steps.identity)
      }
    } catch {
      setFeedback('error')
      setFeedbackUpdatedAt(queryClient.getQueryState(verificationKeys.view)?.dataUpdatedAt ?? null)
    } finally {
      setIsSyncing(false)
    }
  }

  async function pollAfterExit(initialIdentity: KycOnboarding['steps']['identity']) {
    const startedAt = Date.now()
    while (Date.now() - startedAt < POST_EXIT_POLL_LIMIT_MS) {
      await wait(POST_EXIT_POLL_INTERVAL_MS)
      if (Date.now() - startedAt >= POST_EXIT_POLL_LIMIT_MS) return
      try {
        const view = await queryClient.fetchQuery({
          queryKey: verificationKeys.view,
          queryFn: fetchKycVerification,
        })
        const identity = view.onboarding.steps.identity
        if (identity !== initialIdentity) {
          setFeedback(
            feedbackFromView(
              view.onboarding,
              Boolean(view.verification?.syncFailure),
              view.verification?.requirements.embedded.length ?? 0,
            ),
          )
          setFeedbackUpdatedAt(
            queryClient.getQueryState(verificationKeys.view)?.dataUpdatedAt ?? null,
          )
          return
        }
      } catch {
        return
      }
    }
  }

  return {
    isOpen,
    session,
    sessionKey,
    isLoading,
    isSyncing,
    error,
    registrationFeedbackEnabled,
    feedback,
    feedbackUpdatedAt,
    open,
    retry,
    close,
  }
}

function describeOpeningError(error: unknown): string {
  if (error instanceof AppError) {
    if ([401, 403, 429].includes(error.status)) return getUserFacingError(error).description
    if (error.status === 409 && error.code === 'step_unavailable') {
      return 'Esta etapa não está disponível no momento. Confira as pendências do cadastro.'
    }
    if (error.status === 422 && error.code === 'provider_validation') {
      return 'Não foi possível iniciar a verificação com os dados atuais. Confira as pendências do cadastro.'
    }
  }
  return 'Não foi possível abrir esta etapa. Tente novamente em alguns instantes.'
}

export function feedbackFromView(
  onboarding: KycOnboarding,
  hasSyncFailure: boolean,
  embeddedRequirementCount: number,
): Exclude<EmbeddedFeedback, null> {
  if (hasSyncFailure) return 'error'
  if (onboarding.steps.identity === 'in_review') return 'in_review'
  if (onboarding.steps.identity === 'done') return 'done'
  if (
    (onboarding.steps.identity === 'pending' || onboarding.steps.identity === 'fix') &&
    embeddedRequirementCount > 0
  ) {
    return 'needs_action'
  }
  return 'error'
}

function wait(durationMs: number): Promise<void> {
  return new Promise((resolve) => window.setTimeout(resolve, durationMs))
}
